"use client";

import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import { domId } from "@/lib/dom-id";
import { Button, Input } from "@/components/ui";
import { InlineStatus } from "@/components/ui/inline-status";

interface ConfirmDeleteModalProps {
  open: boolean;
  title?: string;
  message?: string;
  onClose: () => void;
  onConfirm: () => void;
  deleting?: boolean;
  error?: string | null;
  onDismissError?: () => void;
}

export default function ConfirmDeleteModal({
  open,
  title,
  message,
  onClose,
  onConfirm,
  deleting = false,
  error,
  onDismissError,
}: ConfirmDeleteModalProps) {
  const t = useTranslations("SystemDeleteModal");
  const [confirmText, setConfirmText] = useState("");
  const keyword = "delete";

  const resolvedTitle = title ?? t("title");
  const resolvedMessage = message ?? t("message");

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setConfirmText("");
    }
  }, [open]);

  // Handle Enter key press
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      console.log(
        "Key pressed:",
        e.key,
        "confirmText:",
        confirmText,
        "canDelete:",
        confirmText === "delete",
      );

      if (e.key === "Enter") {
        e.preventDefault();
        if (confirmText === "delete" && !deleting) {
          console.log("Calling onConfirm");
          onConfirm();
        } else {
          console.log("Cannot delete - text not match or deleting");
        }
      }
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, confirmText, deleting, onConfirm, onClose]);

  if (!open) return null;

  const canDelete = confirmText === keyword && !deleting;

  return (
    <div
      data-testid={domId("mdl", "system-delete-modal")}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <div className="w-full max-w-md rounded-xl bg-white shadow-2xl p-6">
        {/* Header with Icon and Title */}
        <div className="flex items-start gap-3 mb-4">
          <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 border-red-600 mt-0.5">
            <span className="text-red-600 text-xs font-bold leading-none">
              !
            </span>
          </div>
          <div className="flex-1">
            <h2 className="text-base font-semibold text-gray-900">
              {resolvedTitle}
            </h2>
          </div>
        </div>

        {/* Message */}
        <p className="text-sm text-gray-600 mb-4 leading-relaxed">
          {resolvedMessage}
        </p>

        {/* Confirmation Input */}
        <div className="mb-4">
          <p className="text-sm text-gray-700 mb-2">
            {t("prompt", { keyword })}
          </p>
          <Input
            data-testid={domId("txt", "system-delete-confirm-text")}
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={t("placeholder", { keyword })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
            disabled={deleting}
            autoFocus
          />
        </div>

        {/* Error Message */}
        {error && (
          <InlineStatus
            purpose="system-delete-modal-error"
            status="error"
            className="mb-4"
            onDismiss={onDismissError}
          >
            {error}
          </InlineStatus>
        )}

        {/* Buttons */}
        <div className="flex justify-end gap-2">
          <Button
            data-testid={domId("btn", "system-delete-cancel")}
            type="button"
            onClick={onClose}
             variant="unstyled" className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition disabled:opacity-50"
            disabled={deleting}
          >
            {t("actions.cancel")}
          </Button>
          <Button
            data-testid={domId("btn", "system-delete-confirm")}
            type="button"
            onClick={onConfirm}
             variant="unstyled" className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!canDelete}
            aria-busy={deleting}
            data-loading={deleting ? "true" : undefined}
          >
            {t("actions.delete")}
          </Button>
        </div>
      </div>
    </div>
  );
}
